import {startModalProcessing, stopModalLoading} from './../../reducers/environmentReducer';
import {type Transaction, type OrderService, displayPaymentErrorMessage, requestCartCalculation} from './../order';
import {type PayPalNamespace, type PayPalButtonsComponent, type CreateOrderData, type CreateOrderActions, type OnApproveData, type OnApproveActions, type FUNDING_SOURCE, type PayPalButtonsComponentOptions, type OnCancelledActions} from '@paypal/paypal-js';
import {$qs} from '../../../../@shared/ts/dom';
import {getErrorString} from '../../../../@shared/ts/error';
import {FeatureFlag} from '../../../../@type/features';
import {Feature} from '../../reducers/environmentReducer';
import {type PayPalPaymentData} from './paypal';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {type IResponse} from '../../../../@type/response';

class PayPalPaymentButton {
	private readonly paypal: PayPalNamespace;
	private readonly orderService: OrderService;
	private readonly gatewayId: string;
	private readonly buttonStyle: PayPalButtonsComponentOptions['style'];
	private readonly fundingType: FUNDING_SOURCE;

	private button: PayPalButtonsComponent | null;
	private mountContainer: HTMLDivElement | null;
	private isMounted: boolean;

	private currentPaymentDetails: PayPalPaymentData | null;
	private currentTransaction: Transaction | null;

	constructor(paypal: PayPalNamespace, orderService: OrderService, options: {
		gatewayId: string;
		fundingType: FUNDING_SOURCE;
		buttonStyle: PayPalButtonsComponentOptions['style'];
	}) {
		this.paypal = paypal;
		this.orderService = orderService;
		this.gatewayId = options.gatewayId;
		this.fundingType = options.fundingType;
		this.buttonStyle = options.buttonStyle;

		this.isMounted = false;
		this.mountContainer = null;

		this.currentPaymentDetails = null;
		this.currentTransaction = null;

		this.button = this.createButtonComponent();
	}

	public isEligible(): boolean {
		return this.button?.isEligible() ?? false;
	}

	public mount(selector: string) {
		if (this.isMounted) {
			return;
		}

		const $container = $qs(selector);
		if (!$container) {
			return;
		}

		if (!this.button) {
			this.button = this.createButtonComponent();
		}

		if (this.mountContainer) {
			this.mountContainer?.classList.remove('hide');
		} else {
			this.mountContainer = document.createElement('div');
			$container.insertAdjacentElement('beforeend', this.mountContainer);
			this.button?.render(this.mountContainer).catch(() => { /**/});
		}

		this.isMounted = true;
	}

	public unmount() {
		if (!this.isMounted) {
			return;
		}

		this.mountContainer?.remove();
		this.mountContainer = null;
		this.button = null;
		this.isMounted = false;
	}

	private createButtonComponent(): PayPalButtonsComponent | null {
		const {orderService} = this;
		const gateway = this;
		// eslint-disable-next-line new-cap
		return this.paypal?.Buttons?.({
			fundingSource: this.fundingType,
			style: this.buttonStyle,
			onClick() {
				store.dispatch(startModalProcessing());
			},
			async createOrder(_data: CreateOrderData, _actions: CreateOrderActions) {
				try {
					const {error: transactionError, result: transaction} = await orderService.startTransaction(gateway.gatewayId);
					if (transactionError || !transaction) {
						const errorMessage = transactionError ? getErrorString(transactionError) : getLocaleText('An unknown error occured while starting the transaction. Please refresh the page and try again.');
						displayPaymentErrorMessage(errorMessage);

						store.dispatch(stopModalLoading());
						return '';
					}

					gateway.currentTransaction = transaction;

					const {error: orderError, result: orderResult} = await orderService.placeOrder(gateway.currentTransaction);

					if (orderError || !orderResult || orderResult.result !== 'success') {
						store.dispatch(stopModalLoading());
						return '';
					}

					const dataURL = new URL(orderResult.redirect);
					const [name, data] = dataURL.hash.split('=');
					if (name !== '#payment_data' || !data) {
						await gateway.currentTransaction.complete({
							note: 'Failed to retrieve paypal payment details from url: ' + orderResult.redirect,
						});

						// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
						// possible problems we will get a new cart calculation.
						await requestCartCalculation();

						store.dispatch(stopModalLoading());
						return '';
					}

					const paymentData = JSON.parse(atob(decodeURIComponent(data))) as PayPalPaymentData;

					gateway.currentPaymentDetails = paymentData;
					return paymentData.data.id;
				} catch (error) {
					await gateway.currentTransaction?.complete({
						note: 'Failed creating paypal order: ' + getErrorString(error),
					});

					// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
					// possible problems we will get a new cart calculation.
					await requestCartCalculation();

					store.dispatch(stopModalLoading());
					return '';
				}
			},
			async onApprove(_data: OnApproveData, actions: OnApproveActions) {
				if (!gateway.currentPaymentDetails?.order_id) {
					await gateway.currentTransaction?.complete({
						note: 'Failed approving paypal order: "Missing payment details"',
					});
					actions.restart();

					// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
					// possible problems we will get a new cart calculation.
					await requestCartCalculation();

					store.dispatch(stopModalLoading());
					return;
				}

				const approveURL = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'approve_order_url');

				if (!approveURL) {
					await gateway.currentTransaction?.complete({
						note: 'Failed approving paypal order: "Missing required approve URL or approve URL security nonce"',
					});
					actions.restart();

					// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
					// possible problems we will get a new cart calculation.
					await requestCartCalculation();

					store.dispatch(stopModalLoading());
					return;
				}

				const formData = new FormData();
				formData.append('order_id', gateway.currentPaymentDetails.order_id);

				const response = await fetch(approveURL, {
					method: 'POST',
					body: formData,
				});

				try {
					const json = await response.json() as IResponse<void>;
					if (!json.success) {
						await gateway.currentTransaction?.complete({
							note: json.message,
						});
						actions.restart();

						// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
						// possible problems we will get a new cart calculation.
						await requestCartCalculation();

						store.dispatch(stopModalLoading());
						return;
					}
				} catch (error) {
					const paypalOrder = await actions.order?.get();
					if (paypalOrder?.status !== 'APPROVED' && paypalOrder?.status !== 'COMPLETED') {
						await gateway.currentTransaction?.complete({
							note: `PayPal order failed to finish: ${getErrorString(error)}`,
						});
						actions.restart();

						// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
						// possible problems we will get a new cart calculation.
						await requestCartCalculation();

						store.dispatch(stopModalLoading());
						return;
					}
				}

				if (window.top) {
					window.top.location.href = gateway.currentPaymentDetails.success_url;
				} else {
					actions.redirect(gateway.currentPaymentDetails.success_url);
				}
			},
			async onCancel(_data: Record<string, unknown>, _actions: OnCancelledActions) {
				if (!gateway.currentPaymentDetails) {
					await gateway.currentTransaction?.complete({
						paymentStatus: 'CANCELED',
						orderStatus: 'unknown',
						note: 'Payment canceled by customer. Order status "unknown" because of missing payment details.',
					});

					// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
					// possible problems we will get a new cart calculation.
					await requestCartCalculation();

					store.dispatch(stopModalLoading());
					return;
				}

				await gateway?.currentTransaction?.complete({
					paymentStatus: 'CANCELED',
					orderStatus: 'canceled',
				});

				cancelOrder(gateway.currentPaymentDetails.cancel_url);

				store.dispatch(stopModalLoading());
			},
			async onError(error: Record<string, unknown>) {
				await gateway.currentTransaction?.complete({
					note: getErrorString(error),
				});

				gateway.currentTransaction = null;
				gateway.currentPaymentDetails = null;

				// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
				// possible problems we will get a new cart calculation.
				await requestCartCalculation();

				store.dispatch(stopModalLoading());
			},
		}) ?? null;
	}
}

function cancelOrder(url: string) {
	try {
		const xhr = new XMLHttpRequest();
		xhr.open('GET', url);
		xhr.send();
	} catch {
		// Do no harm
	}
}

export {
	PayPalPaymentButton,
};
